<template>
  <template v-if="typeContextCourant === TypesContextesAutorisesEnum.Prestataire">
    <BasicText>
      En tant que prestataire vous pouvez pré-saisir une demande de formation et la soumettre à l’employeur pour
      validation.
    </BasicText>
    <BasicText>
      Pour votre information, l'adresse e-mail de l'employeur est indispensable lors de la saisie de la demande. Elle
      permettra l'envoi d'un e-mail informant l'employeur de la pré-saisie de la demande de formation. Celui-ci pourra
      ainsi vérifier l'exactitude des données saisies et aura la possibilité d'accepter ou de refuser la demande.
    </BasicText>
    <BasicText>
      Si la demande est acceptée et transmise à AKTO, vous recevrez une notification d’accord. En revanche, si
      l'employeur refuse de reprendre la demande, vous en serez informé.
    </BasicText>
    <BasicText>Pour vous aider dans la saisie de la demande, vous trouverez ci-dessous des liens utiles.</BasicText>
    <BasicText>
      <ul>
        <li
          v-for="(urlLabel, i) in urlLabelsPrestataire"
          :key="i"
        >
          <BasicLink
            :label="urlLabel.label"
            @link-click="redirectExterne(urlLabel.url)"
          />
        </li>
      </ul>
    </BasicText>
  </template>
  <template v-else>
    <BasicText v-if="model?.sousDispositif === 'ContratProfessionnalisation'">
      Avant de conclure un contrat de professionnalisation, renseignez-vous sur les règles applicables ainsi que les
      dispositions particulières prévues par votre convention collective ou les accords collectifs de branche (Exemple :
      la rémunération, la durée maximale du contrat et de la formation ou bien le montant du forfait de formation).
      N’hésitez pas à vous rapprocher de votre conseiller.
    </BasicText>

    <BasicText v-if="model?.sousDispositif === 'Prodiat'">
      <BasicText weight="bold">PRODIAT</BasicText>
      est un contrat de professionnalisation dont le parcours de formation en alternance est défini avec l’aide d’un
      organisme de formation dit « architecte » (OFA) sélectionné par AKTO sur marché public.
      <br />
      Renseignez-vous sur les règles applicables ainsi que les dispositions particulières prévues par votre convention
      collective ou les accords collectifs de branche (Exemple : la rémunération, la durée maximale du contrat ou bien
      le montant du forfait de formation). N’hésitez pas à vous rapprocher de votre conseiller.
    </BasicText>

    <BasicText v-if="model?.sousDispositif === 'ContratProfessionnalisationHope'">
      <BasicText weight="bold">Le programme HOPE</BasicText>
      permet d’accompagner l’accès à l’emploi et à l’autonomie des réfugiés et s’appuie sur un contrat de
      professionnalisation. Renseignez-vous sur les règles applicables ainsi que les dispositions particulières prévues
      par votre convention collective ou les accords collectifs de branche (Exemple : la rémunération, la durée maximale
      d u contrat ou bien le montant du forfait de formation). N’hésitez pas à vous rapprocher de votre conseiller.
    </BasicText>

    <BasicText v-if="model?.sousDispositif === 'Prodiat'">
      <BasicText weight="bold">AKTO</BasicText>
      peut financer les actions d’après les critères définis par votre branche professionnelle en lien avec ses
      priorités de formation.
    </BasicText>

    <BasicText>
      Pour vous aider dans la compréhension du dispositif et la saisie de la demande, vous trouverez ci-dessous des
      liens utiles.
    </BasicText>

    <BasicText>
      <ul>
        <li
          v-for="(urlLabel, i) in urlLabels"
          :key="i"
        >
          <BasicLink
            :label="urlLabel.label"
            @link-click="redirectExterne(urlLabel.url)"
          />
        </li>
      </ul>
    </BasicText>
  </template>
  <WrapperCheckbox
    v-model="validationNotice"
    :alertLabel="meta.validationNotice?.$messageErreurDirty!"
    :readonly
  >
    <BasicText>
      J'ai pris connaissance de la
      <BasicLink
        label="notice"
        @link-click="redirectExterne('https://www.formulaires.service-public.fr/gf/getNotice.do?cerfaNotice=51650&cerfaFormulaire=12434')"
      />
      comprenant les informations liées au traitement des données à caractère personnel.
    </BasicText>
  </WrapperCheckbox>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
  import { type UrlLabel } from '@/features/interface/shared/tools'
  import { useDemandeCPROStore } from '../../useDemandeCPROStore'
  import { redirectExterne } from '@/features/shared/tools/url'

  const store = useDemandeCPROStore()
  const { model, meta, readonly, typeContextCourant } = storeToRefs(store)
  const { setupFieldWithKey } = store
  const validationNotice = setupFieldWithKey<boolean>('validationNotice')

  const urlLabels = computed(() => {
    const urlsLabels: UrlLabel[] = []
    urlsLabels.push({
      label: 'Les règles de prise en charge',
      url: 'https://www.akto.fr/financer-une-formation/regles-de-prise-en-charge/',
    })
    urlsLabels.push({
      label: 'Tuteur et critères d’éligibilité',
      url: 'https://www.akto.fr/recruter-en-alternance/le-tutorat/',
    })
    if (model.value?.sousDispositif === 'Prodiat')
      urlsLabels.push({
        label: 'Contrat de professionnalisation Prodiat',
        url: 'https://www.akto.fr/prodiat/',
      })
    else
      urlsLabels.push({
        label: 'Contrat de professionnalisation site AKTO',
        url: 'https://www.akto.fr/contrat-professionnalisation/',
      })
    urlsLabels.push({
      label: 'Contrat de professionnalisation site travail.emploi.gouv',
      url: 'https://travail-emploi.gouv.fr/le-contrat-de-professionnalisation',
    })

    if (model.value?.sousDispositif === 'ContratProfessionnalisationHope')
      urlsLabels.push({
        label: 'Le programme Hope',
        url: 'https://www.akto.fr/hope/',
      })

    urlsLabels.push({
      label: 'Le mode opératoire',
      url: 'https://ressourcesstatiques.akto.fr/guide/GuideContratProfessionnalisation.pdf',
    })
    return urlsLabels
  })

  const urlLabelsPrestataire = computed(() => {
    const urlsLabels: UrlLabel[] = []

    urlsLabels.push({
      label: 'Le mode opératoire',
      url: 'https://ressourcesstatiques.akto.fr/guide/GuideContratProfessionnalisationPrestataire.pdf',
    })
    return urlsLabels
  })
</script>
